<template>
  <div class="editor-container">
    <div class="editor-header">
      <div class="widget-type">
        <router-link
          :to="{ name: 'pdf_report_editor', params: { report_id: reportId } }">
          <b-button
            type="is-light"
            class="back-btn mr-5"
            icon-right="arrow-left" />
        </router-link>
        <div class="report-breadcrumbs">
          <div>
            <h4>Report</h4>
            <h2>{{ reportData?.name || "Pdf Report" }}</h2>
          </div>

          <div class="arrow-right">
            <i class="material-icons">chevron_right </i>
          </div>

          <div>
            <h4>Module</h4>
            <h2>{{ module?.title || moduleConfigs.name }}</h2>
          </div>
        </div>
      </div>

      <div class="controls">
        <a-button
          type="button ml-2"
          :disabled="!isDirty"
          :loading="isSaving"
          hotkeys="ctrl+shift+s"
          class="button is-primary"
          @click="save">
          Publish Changes
        </a-button>
        <a-button
          type="button ml-2"
          :disabled="!isDirty"
          hotkeys="ctrl+shift+d"
          class="button is-light"
          @click="getData">
          Discard Changes
        </a-button>
      </div>
    </div>
    <div class="editor-body">
      <div class="editor-and-preview">
        <splitpanes class="default-theme">
          <pane
            min-size="20"
            class="pane">
            <div
              v-if="module"
              class="block editor p-5">
              <b-field
                label="Title"
                class="input-field"
                message="Shown in the module list only — the report never prints it.">
                <b-input v-model="module.title" />
              </b-field>

              <h5 class="section-label">
                What to summarize
              </h5>
              <p class="hint">
                A summary can cover this report, specific modules in it, or
                other reports entirely — including reports nobody is sent.
                Summarizer modules are never summarized, so pointing this at
                its own report is safe.
              </p>

              <b-field>
                <b-checkbox v-model="module.summarize_whole_report">
                  Summarize this whole report
                </b-checkbox>
              </b-field>

              <b-field
                v-if="!module.summarize_whole_report"
                label="Modules in this report"
                class="input-field">
                <b-taginput
                  v-model="selectedModules"
                  :data="moduleChoices"
                  autocomplete
                  open-on-focus
                  field="label"
                  placeholder="Pick modules to summarize"
                  @typing="filterModules" />
              </b-field>

              <b-field
                label="Other reports"
                class="input-field"
                message="Loaded and rendered on demand, whether or not they
                  are attached to the email.">
                <b-taginput
                  v-model="selectedReports"
                  :data="reportChoices"
                  autocomplete
                  open-on-focus
                  field="label"
                  placeholder="Pick reports to summarize"
                  @typing="filterReports" />
              </b-field>

              <div
                v-if="targetReports.length"
                class="target-report-modules">
                <p class="hint">
                  Each target report is read whole unless you narrow it.
                  Modules you leave out are never loaded.
                </p>
                <b-field
                  v-for="report in targetReports"
                  :key="report.key"
                  :label="report.label"
                  class="input-field">
                  <b-taginput
                    :value="selectedReportModules(report.key)"
                    :data="reportModuleChoices[report.key]
                      || reportModuleOptions(report.key)"
                    autocomplete
                    open-on-focus
                    field="label"
                    placeholder="All modules"
                    @input="chosen => setReportModules(report.key, chosen)"
                    @typing="text => filterReportModules(report.key, text)" />
                </b-field>
              </div>

              <h5 class="section-label">
                Model
              </h5>
              <p class="hint">
                Leave any of these blank to inherit the report's or company's
                configured value.
              </p>

              <b-field
                label="Model"
                class="input-field">
                <b-select
                  v-model="module.model"
                  expanded>
                  <option :value="null">
                    Inherit
                  </option>
                  <option
                    v-for="m in models"
                    :key="m.value"
                    :value="m.value">
                    {{ m.value }} — {{ m.price }} / 1M
                  </option>
                </b-select>
              </b-field>

              <b-field
                label="Thinking budget"
                class="input-field"
                message="Tokens the model may reason with before answering.
                  0 disables thinking; -1 lets it decide. Billed as output.">
                <b-input
                  v-model.number="module.thinking_budget"
                  type="number"
                  placeholder="Inherit" />
              </b-field>

              <b-field
                label="Max output tokens"
                class="input-field">
                <b-input
                  v-model.number="module.max_output_tokens"
                  type="number"
                  placeholder="Inherit" />
              </b-field>

              <h5 class="section-label">
                Wording
              </h5>
              <p class="hint">
                The three guides shape every summary on the platform. Leave a
                box empty to use the platform default shown as its
                placeholder; anything typed here applies to this module only.
              </p>

              <div
                v-for="guide in guideFields"
                :key="guide.key"
                class="guide">
                <div class="guide-header">
                  <strong>{{ guide.label }}</strong>
                  <span class="guide-note">{{ guide.note }}</span>
                  <span
                    v-if="module[guide.key]"
                    class="guide-edited">overridden</span>
                </div>
                <textarea
                  v-model="module[guide.key]"
                  class="textarea is-small guide-input"
                  :placeholder="defaultGuides[guide.key]"
                  :rows="guide.rows" />
              </div>

              <b-field
                label="Instructions for this summary"
                class="input-field"
                message="Appended after the guides — use it for what this one
                  summary should lead with.">
                <b-input
                  v-model="module.instructions"
                  type="textarea"
                  rows="3" />
              </b-field>

              <h5 class="section-label">
                Presentation
              </h5>

              <b-field class="input-field">
                <b-checkbox v-model="module.show_heading">
                  Show a heading above the summary
                </b-checkbox>
              </b-field>

              <b-field
                v-if="module.show_heading"
                label="Heading"
                class="input-field">
                <b-input v-model="module.heading" />
              </b-field>
            </div>
          </pane>
          <pane min-size="20">
            <PreviewAndJSONEditor
              v-model="module"
              type="summarizer"
              :full-report="reportData"
              :default-time-range="reportData?.default_time_range" />
          </pane>
        </splitpanes>
      </div>
    </div>
    <b-loading
      v-model="isLoading"
      :is-full-page="true"
      :can-cancel="false" />

    <b-modal
      v-model="showUnsavedModal"
      :destroy-on-hide="true"
      trap-focus
      close-button-aria-label="Close"
      aria-modal
      custom-class="unsaved-modal-simple">
      <div class="unsaved-modal-content">
        <div class="unsaved-modal-title-row">
          <span class="unsaved-modal-title">You have unsaved changes</span>
        </div>
        <div class="unsaved-modal-body">
          <p>Do you want to save your changes before leaving this page?</p>
        </div>
        <div class="unsaved-modal-actions custom-unsaved-modal-actions">
          <b-button
            type="is-outlined"
            size="is-small"
            class="cancel-btn"
            @click="closeUnsavedModal">
            Cancel
          </b-button>
          <div class="right-actions">
            <b-button
              type="is-muted"
              size="is-small"
              class="discard-btn"
              @click="discardAndNavigate">
              Discard
            </b-button>
            <b-button
              size="is-small"
              class="save-btn"
              @click="saveAndNavigate">
              Save Changes
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import { Splitpanes, Pane } from 'splitpanes'
  import PreviewAndJSONEditor from '@/components/pdf_report/PreviewAndJsonEditor.vue'

  // Mirrors report-exporters ALLOWED_MODELS, default first so the picker's
  // order reads as a recommendation. The backend re-validates, so this is a
  // convenience rather than the authority. Prices are per 1M tokens (in/out).
  const MODELS = [
    { value: 'gemini-3.1-flash-lite', price: '$0.25 / $1.50', },
    { value: 'gemini-2.5-flash', price: '$0.30 / $2.50', },
    { value: 'gemini-2.5-flash-lite', price: '$0.10 / $0.40', },
    { value: 'gemini-2.5-pro', price: '$1.25 / $10.00', },
    { value: 'gemini-3-flash-preview', price: '$0.50 / $3.00', },
    { value: 'gemini-3.1-pro-preview', price: '$2.00 / $12.00', },
    { value: 'gemini-3.5-flash', price: '$1.50 / $9.00', },
  ]

  const GUIDE_FIELDS = [
    {
      key: 'format_guide',
      label: 'Format',
      note: 'Shape and length',
      rows: 6,
    },
    {
      key: 'tone_guide',
      label: 'Tone',
      note: 'Voice and stance',
      rows: 8,
    },
    {
      key: 'industry_guide',
      label: 'Industry',
      note: 'What the numbers mean',
      rows: 10,
    },
  ]

  export default {
    name: 'SummarizerEditor',
    components: {
      Splitpanes,
      Pane,
      PreviewAndJSONEditor,
    },

    beforeRouteLeave (to, from, next) {
      if (this.isDirty) {
        this.showUnsavedModal = true
        this.nextRoute = to
      } else {
        next()
      }
    },

    data () {
      return {
        moduleId: null,
        reportId: null,
        module: null,
        reportData: null,

        models: MODELS,
        guideFields: GUIDE_FIELDS,
        // Shown as placeholders so the boxes read as "this is what you get if
        // you leave it alone" rather than as empty prompts.
        defaultGuides: { format_guide: '', tone_guide: '', industry_guide: '', },

        allReports: [],
        moduleChoices: [],
        reportChoices: [],
        // Autocomplete choices per target report, keyed by report key. One
        // shared list would filter every picker at once.
        reportModuleChoices: {},

        moduleConfigs: {
          name: 'Summarizer',
          type: 'SummarizerModule',
          icon: require('@/assets/icons/summarizer.svg'),
        },

        isLoading: false,
        isDirty: false,
        isSaving: false,

        showUnsavedModal: false,
        nextRoute: null,
      }
    },

    computed: {
      // Every module in this report that a summarizer could point at. Other
      // summarizers are excluded: the backend never feeds one summary into
      // another, so offering it here would promise something that cannot
      // happen.
      moduleOptions () {
        return (this.reportData?.modules || [])
          .filter(m => m.type !== 'SummarizerModule')
          .map(m => ({ id: m.id, label: m.title || m.type, }))
      },

      reportOptions () {
        return this.allReports.map(r => ({
          key: r.key,
          label: r.name || r.key,
        }))
      },

      // The target reports that can be narrowed, in the order picked. The
      // own report is excluded: its narrowing control is "Modules in this
      // report" above, and the backend's own-report path ignores
      // target_report_module_ids -- a picker here would silently do nothing.
      targetReports () {
        return (this.module?.target_report_keys || [])
          .filter(key => key !== this.reportData?.key)
          .map(
            key => this.reportOptions.find(o => o.key === key)
              || { key, label: key, }
          )
      },

      // Taginput works in objects, the module stores ids and keys. These two
      // translate between them so the stored config stays portable.
      selectedModules: {
        get () {
          return (this.module?.target_module_ids || [])
            .map(id => this.moduleOptions.find(o => o.id === id)
              || { id, label: id, })
        },
        set (chosen) {
          this.module.target_module_ids = chosen.map(o => o.id)
        },
      },

      selectedReports: {
        get () {
          return (this.module?.target_report_keys || [])
            .map(key => this.reportOptions.find(o => o.key === key)
              || { key, label: key, })
        },
        set (chosen) {
          const keys = chosen.map(o => o.key)
          this.module.target_report_keys = keys

          // Drop the narrowing for any report no longer targeted, so
          // re-picking it later does not silently reapply an old filter.
          const filters = this.module.target_report_module_ids || {}
          Object.keys(filters).forEach(key => {
            if (!keys.includes(key)) this.$delete(filters, key)
          })
        },
      },
    },

    watch: {
      module: {
        handler () {
          this.isDirty = true
        },
        deep: true,
      },
    },

    async mounted () {
      this.moduleId = this.$route.params.module_id
      this.reportId = this.$route.params.report_id

      await this.getData()
      this.isLoading = false

      await this.loadDefaultGuides()
      await this.loadReports()
      await this.$store.dispatch('app/reportsConfig/refreshOptions')
    },

    methods: {
      async getData () {
        this.isLoading = true
        this.module = await this.$apis.pdfReports.getPdfModuleData(this.moduleId)
        this.reportData = await this.$apis.pdfReports.getPdfReportData(
          this.reportId
        )

        this.moduleChoices = this.moduleOptions
        await this.$nextTick()
        this.isDirty = false
        this.isLoading = false
      },

      async loadDefaultGuides () {
        try {
          const response = await this.$apis.pdfReports.getAiSummaryGuides()
          if (response) {
            Object.keys(this.defaultGuides).forEach(key => {
              this.$set(this.defaultGuides, key, response[key] || '')
            })
          }
        } catch (e) {
          // Placeholders are a convenience; the backend applies the same
          // defaults whether or not they were ever displayed.
          console.log('error loading summary guides', e)
        }
      },

      async loadReports () {
        const response = await this.$apis.pdfReports.getAllPdfReports()
        this.allReports = response || []
        this.reportChoices = this.reportOptions
      },

      filterModules (text) {
        const query = (text || '').toLowerCase()
        this.moduleChoices = this.moduleOptions.filter(
          o => o.label.toLowerCase().includes(query)
        )
      },

      filterReports (text) {
        const query = (text || '').toLowerCase()
        this.reportChoices = this.reportOptions.filter(
          o => o.label.toLowerCase().includes(query)
        )
      },

      // The modules of another report, from the list already loaded for the
      // report picker. Summarizers are excluded for the same reason they are
      // in this report: one summary is never fed into another.
      reportModuleOptions (reportKey) {
        const report = this.allReports.find(r => r.key === reportKey)
        return (report?.modules || [])
          .filter(m => m.type !== 'SummarizerModule')
          .map(m => ({ id: m.id, label: m.title || m.type, }))
      },

      selectedReportModules (reportKey) {
        const options = this.reportModuleOptions(reportKey)
        return ((this.module?.target_report_module_ids || {})[reportKey] || [])
          .map(id => options.find(o => o.id === id) || { id, label: id, })
      },

      setReportModules (reportKey, chosen) {
        if (!this.module.target_report_module_ids) {
          this.$set(this.module, 'target_report_module_ids', {})
        }
        const filters = this.module.target_report_module_ids
        if (chosen.length) {
          this.$set(filters, reportKey, chosen.map(o => o.id))
        } else {
          // An empty list and no entry both mean "the whole report", so the
          // key is removed rather than stored empty -- a config that never
          // narrowed anything stays identical to one that never could.
          this.$delete(filters, reportKey)
        }
      },

      filterReportModules (reportKey, text) {
        const query = (text || '').toLowerCase()
        this.$set(
          this.reportModuleChoices,
          reportKey,
          this.reportModuleOptions(reportKey).filter(
            o => o.label.toLowerCase().includes(query)
          )
        )
      },

      async save () {
        this.isSaving = true
        this.isDirty = false
        const response = await this.$apis.pdfReports.updatePdfReportModule(
          this.module
        )
        this.isSaving = false

        if (!response) {
          this.isDirty = true
          this.$buefy.toast.open({
            message: 'Backend Error, Report this to the devs',
            type: 'is-warning',
          })
          return
        }

        if (response?.errors) {
          this.isDirty = true
          this.$buefy.toast.open({
            message: 'Error saving module, Fix the errors and try again',
            type: 'is-warning',
          })
        } else {
          this.module = response
          this.$buefy.toast.open({
            message: 'Changes Saved',
            type: 'is-success',
            position: 'is-bottom',
          })
          await this.$nextTick()
          this.isDirty = false
        }
      },

      closeUnsavedModal () {
        this.showUnsavedModal = false
        this.nextRoute = null
      },
      discardAndNavigate () {
        this.isDirty = false
        this.showUnsavedModal = false
        if (this.nextRoute) {
          this.$router.push(this.nextRoute)
        }
      },
      async saveAndNavigate () {
        await this.save()
        this.showUnsavedModal = false
        if (this.nextRoute) {
          this.$router.push(this.nextRoute)
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
// Indented under the report picker so the pickers read as belonging to the
// reports chosen above them rather than as another top-level setting.
.target-report-modules {
  margin-left: 16px;
  padding-left: 12px;
  border-left: 2px solid $grayscale-6;
}

.report-breadcrumbs {
  display: flex;

  h2 {
    font-weight: 400;
    font-size: 20px;
    margin: 0;
  }

  h4 {
    font-weight: 400;
    font-size: 12px;
    margin: 0;
    color: $grayscale-3;
  }

  .arrow-right {
    display: flex;
    align-items: center;
    margin: 0 10px;
    font-size: 20px;
    color: #b0b0b0;
  }
}

.back-btn {
  font-size: 20px !important;
  border-radius: 100% !important;
}

.editor-container {
  padding: 20px;
  padding-top: 5px;
  width: 100%;
  height: 100%;
  margin: auto !important;
  background-color: white;
  display: flex;
  flex-direction: column;
}

.editor-body {
  display: flex;
  height: 100%;
  overflow: hidden;
}

.block {
  border: $border-1;
  border-radius: 3px;
  height: 100%;
  flex-direction: column;
  display: flex;

  &.editor {
    width: 100%;
    overflow-y: auto;
  }
}

.editor-and-preview {
  width: 100%;
}

.section-label {
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $grayscale-3;
  margin: 24px 0 4px;
}

.hint {
  font-size: 12px;
  color: $grayscale-3;
  margin-bottom: 10px;
}

.guide {
  margin-bottom: 12px;

  .guide-header {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 4px;
    font-size: 12px;
  }

  .guide-note {
    color: $grayscale-3;
  }

  .guide-edited {
    margin-left: auto;
    color: $primary;
    font-size: 11px;
  }

  .guide-input {
    font-size: 12px;
    font-family: monospace;
  }
}

.editor-header {
  margin-bottom: 20px;
  display: flex;
  padding-top: 10px;

  .widget-type {
    display: flex;
    font-weight: 400;
    font-size: 15px;
    align-items: center;

    img {
      width: 35px;
      height: 35px;
      margin-right: 10px;
      border: $border-1;
      border-radius: 8px;
      padding: 5px;
      background-color: rgba(0, 0, 0, 0.05);
    }
  }

  .controls {
    margin-left: auto;
    display: flex;
    align-items: center;

    .button {
      padding: 0 15px;
      font-size: 12px;
    }
  }
}

/* Unsaved changes modal styles (copied from PdfReportEditor.vue) */
.unsaved-modal-simple {
  .modal-content {
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
}
.unsaved-modal-content {
  background: #fff;
  border-radius: 8px;
  padding: 24px 24px 16px 24px;
  min-width: 320px;
  max-width: 400px;
  margin: 0 auto;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.unsaved-modal-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.unsaved-modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #222;
}
.unsaved-modal-body {
  font-size: 15px;
  color: #444;
  margin-bottom: 18px;
}
.unsaved-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.custom-unsaved-modal-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0;
  padding-top: 4px;
}
.custom-unsaved-modal-actions .cancel-btn {
  min-width: 90px;
  border-radius: 4px !important;
  color: black !important;
  font-size: 14px !important;
  border: 1px solid #e0e0e0 !important;
}
.custom-unsaved-modal-actions .right-actions {
  display: flex;
  gap: 8px;
}
.custom-unsaved-modal-actions .discard-btn {
  background: #f5f5f5 !important;
  color: black !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 4px !important;
  font-size: 14px !important;
}
.custom-unsaved-modal-actions .save-btn {
  background: #111 !important;
  color: #fff !important;
  border: 1px solid #111 !important;
  border-radius: 4px !important;
  font-size: 14px !important;
}
</style>
